import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import type {
  PrintCardDesignPage,
  PrintCardElement,
  PrintCardSideDesign,
  SmartGuidesView,
} from "../../print-cards.interface";
import {
  PRINT_CARD_CM_TO_PX,
  printCardSnapThresholdCm,
  snapPrintCardMove,
  snapPrintCardResize,
} from "../../print-card.util";

const CM_TO_PX = PRINT_CARD_CM_TO_PX;
const STAGE_PAD_X = 48;
const STAGE_PAD_Y = 48;
const MIN_FIT_SCALE = 0.15;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;

@Component({
  selector: "app-print-card-canvas",
  templateUrl: "./print-card-canvas.component.html",
  styleUrls: ["./print-card-canvas.component.scss"],
  standalone: false,
})
export class PrintCardCanvasComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input({ required: true }) page!: PrintCardDesignPage;
  @Input({ required: true }) side!: PrintCardSideDesign;
  @Input() cardSide: "front" | "back" = "front";
  @Input() selectedElementId: string | null = null;
  @Input() zoom = 1;
  @Input() fontFamily = '"Roboto", Arial, sans-serif';
  @Input() fontCssUrl: string | null = null;
  @Input() companyFontFileUrl: string | null = null;
  @Input() sampleTexts: Partial<Record<string, string>> = {};
  @Input() loading = false;
  @Input() safeZoneInsetMm = 3;
  fontEpoch = 0;
  @Output() canvasClick = new EventEmitter<void>();
  @Output() zoomChange = new EventEmitter<number>();
  @Output() selectElement = new EventEmitter<string>();
  @Output() elementContextMenu = new EventEmitter<{
    id: string;
    clientX: number;
    clientY: number;
  }>();
  @Output() interactionStart = new EventEmitter<void>();
  @Output() interactionEnd = new EventEmitter<void>();
  @Output() moveElement = new EventEmitter<{
    id: string;
    xCm: number;
    yCm: number;
  }>();
  @Output() resizeElement = new EventEmitter<{
    id: string;
    xCm: number;
    yCm: number;
    widthCm: number;
    heightCm: number;
  }>();
  @Output() rotateElement = new EventEmitter<{
    id: string;
    rotationDeg: number;
    xCm?: number;
    yCm?: number;
  }>();

  @ViewChild("wrap") wrapRef?: ElementRef<HTMLElement>;

  fitScale = 1;
  spaceHeld = false;
  isPanning = false;
  interacting = false;
  smartGuides: SmartGuidesView = { lines: [], spacings: [] };
  private resizeObserver?: ResizeObserver;
  private resizeFrameId = 0;
  private centerScrollPending = false;
  private wheelCleanup?: () => void;
  private panStart: {
    pointerId: number;
    clientX: number;
    clientY: number;
    scrollLeft: number;
    scrollTop: number;
  } | null = null;
  private zoomAnchor: {
    contentX: number;
    contentY: number;
    offsetX: number;
    offsetY: number;
    fromZoom: number;
  } | null = null;
  private injectedFontNode: HTMLLinkElement | HTMLStyleElement | null = null;
  private fontReadyToken = 0;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) {}

  get visibleElements(): PrintCardElement[] {
    return [...this.side.elements].sort((a, b) => a.zIndex - b.zIndex);
  }

  get displayScale(): number {
    return Math.max(
      MIN_FIT_SCALE,
      Math.round(this.fitScale * this.zoom * 1000) / 1000,
    );
  }

  get hasSmartGuides(): boolean {
    return (
      this.smartGuides.lines.length > 0 || this.smartGuides.spacings.length > 0
    );
  }

  get showSafeZone(): boolean {
    return this.interacting && this.safeZoneInsetMm > 0 && !this.loading;
  }

  get safeZoneBox(): {
    left: number;
    top: number;
    width: number;
    height: number;
  } {
    const insetCm = this.safeZoneInsetMm / 10;
    return {
      left: this.guideX(insetCm),
      top: this.guideY(insetCm),
      width: this.guideW(this.page.widthCm - insetCm * 2),
      height: this.guideH(this.page.heightCm - insetCm * 2),
    };
  }

  private snapPage(): {
    widthCm: number;
    heightCm: number;
    originXCm: number;
    originYCm: number;
  } {
    const insetCm = this.safeZoneInsetMm / 10;
    return {
      originXCm: insetCm,
      originYCm: insetCm,
      widthCm: this.page.widthCm - insetCm * 2,
      heightCm: this.page.heightCm - insetCm * 2,
    };
  }

  guideX(cm: number): number {
    return this.page.widthCm > 0 ? (cm / this.page.widthCm) * 100 : 0;
  }

  guideY(cm: number): number {
    return this.page.heightCm > 0 ? (cm / this.page.heightCm) * 100 : 0;
  }

  guideW(cm: number): number {
    return this.page.widthCm > 0 ? (cm / this.page.widthCm) * 100 : 0;
  }

  guideH(cm: number): number {
    return this.page.heightCm > 0 ? (cm / this.page.heightCm) * 100 : 0;
  }

  spacingLabel(sizeCm: number): string {
    const rounded = Math.round(sizeCm * 100) / 100;
    return `${rounded.toFixed(2)} cm`;
  }

  onLayerInteractionStart(): void {
    this.interacting = true;
    this.interactionStart.emit();
  }

  onLayerMove(event: {
    id: string;
    xCm: number;
    yCm: number;
    snap?: boolean;
    measure?: boolean;
  }): void {
    const moving = this.side.elements.find((el) => el.id === event.id);
    if (!moving) {
      this.moveElement.emit(event);
      return;
    }
    const result = snapPrintCardMove({
      moving: {
        id: moving.id,
        xCm: event.xCm,
        yCm: event.yCm,
        widthCm: moving.widthCm,
        heightCm: moving.heightCm,
        rotationDeg: moving.rotationDeg,
      },
      siblings: this.side.elements,
      page: this.snapPage(),
      thresholdCm: printCardSnapThresholdCm(this.displayScale),
      enabled: event.snap !== false,
      measure: Boolean(event.measure),
    });
    this.smartGuides = result.view;
    this.moveElement.emit({
      id: event.id,
      xCm: result.xCm,
      yCm: result.yCm,
    });
  }

  onLayerResize(event: {
    id: string;
    xCm: number;
    yCm: number;
    widthCm: number;
    heightCm: number;
    handle?: string;
    snap?: boolean;
    measure?: boolean;
  }): void {
    const moving = this.side.elements.find((el) => el.id === event.id);
    if (!moving) {
      this.resizeElement.emit(event);
      return;
    }
    const keepSquare = moving.type === "qr" || moving.type === "nfc";
    const result = snapPrintCardResize({
      moving: {
        id: moving.id,
        xCm: event.xCm,
        yCm: event.yCm,
        widthCm: event.widthCm,
        heightCm: event.heightCm,
        rotationDeg: moving.rotationDeg,
      },
      siblings: this.side.elements,
      page: this.snapPage(),
      thresholdCm: printCardSnapThresholdCm(this.displayScale),
      enabled: event.snap !== false,
      measure: Boolean(event.measure),
      handle: event.handle ?? "",
      keepSquare,
    });
    this.smartGuides = result.view;
    this.resizeElement.emit({
      id: event.id,
      xCm: result.xCm,
      yCm: result.yCm,
      widthCm: result.widthCm,
      heightCm: result.heightCm,
    });
  }

  onLayerInteractionEnd(): void {
    this.interacting = false;
    this.smartGuides = { lines: [], spacings: [] };
    this.interactionEnd.emit();
  }

  ngAfterViewInit(): void {
    this.bindResizeObserver();
    this.bindWheelZoom();
    this.scheduleUpdateFitScale(true);
    this.syncEditorFontFace();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const hasZoomAnchor = Boolean(changes["zoom"] && this.zoomAnchor);
    if (changes["page"] || changes["zoom"] || changes["loading"]) {
      this.scheduleUpdateFitScale(
        Boolean(changes["page"] || (changes["zoom"] && !hasZoomAnchor)),
      );
    }
    if (hasZoomAnchor) {
      this.applyZoomAnchor(this.zoom);
    }
    if (
      changes["fontFamily"] ||
      changes["fontCssUrl"] ||
      changes["companyFontFileUrl"]
    ) {
      this.syncEditorFontFace();
    }
  }

  ngOnDestroy(): void {
    this.fontReadyToken += 1;
    if (this.resizeFrameId) {
      cancelAnimationFrame(this.resizeFrameId);
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.wheelCleanup?.();
    this.wheelCleanup = undefined;
    this.injectedFontNode?.remove();
    this.injectedFontNode = null;
    this.endPan();
  }

  onStageClick(): void {
    if (this.isPanning || this.spaceHeld) {
      return;
    }
    this.canvasClick.emit();
  }

  onWrapPointerDown(event: PointerEvent): void {
    const shouldPan =
      event.button === 1 || (event.button === 0 && this.spaceHeld);
    if (!shouldPan || this.loading) {
      return;
    }
    const wrap = this.wrapRef?.nativeElement;
    if (!wrap) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isPanning = true;
    this.panStart = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      scrollLeft: wrap.scrollLeft,
      scrollTop: wrap.scrollTop,
    };
    wrap.setPointerCapture?.(event.pointerId);
    this.cdr.markForCheck();
  }

  onWrapPointerMove(event: PointerEvent): void {
    if (!this.panStart || event.pointerId !== this.panStart.pointerId) {
      return;
    }
    const wrap = this.wrapRef?.nativeElement;
    if (!wrap) {
      return;
    }
    event.preventDefault();
    wrap.scrollLeft =
      this.panStart.scrollLeft - (event.clientX - this.panStart.clientX);
    wrap.scrollTop =
      this.panStart.scrollTop - (event.clientY - this.panStart.clientY);
  }

  onWrapPointerUp(event: PointerEvent): void {
    if (!this.panStart || event.pointerId !== this.panStart.pointerId) {
      return;
    }
    this.endPan();
  }

  @HostListener("window:keydown", ["$event"])
  onWindowKeydown(event: KeyboardEvent): void {
    if (
      event.code !== "Space" ||
      event.repeat ||
      this.isEditableTarget(event)
    ) {
      return;
    }
    event.preventDefault();
    if (!this.spaceHeld) {
      this.spaceHeld = true;
      this.cdr.markForCheck();
    }
  }

  @HostListener("window:keyup", ["$event"])
  onWindowKeyup(event: KeyboardEvent): void {
    if (event.code !== "Space") {
      return;
    }
    if (this.spaceHeld) {
      this.spaceHeld = false;
      if (!this.panStart) {
        this.isPanning = false;
      }
      this.cdr.markForCheck();
    }
  }

  @HostListener("window:blur")
  onWindowBlur(): void {
    this.spaceHeld = false;
    this.endPan();
  }

  private bindWheelZoom(): void {
    const wrap = this.wrapRef?.nativeElement;
    if (!wrap || this.wheelCleanup) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }
      event.preventDefault();
      const direction = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      const next = this.clampZoom(this.zoom + direction);
      if (next === this.zoom) {
        return;
      }
      const rect = wrap.getBoundingClientRect();
      this.zoomAnchor = {
        contentX: event.clientX - rect.left + wrap.scrollLeft,
        contentY: event.clientY - rect.top + wrap.scrollTop,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        fromZoom: this.zoom,
      };
      this.ngZone.run(() => this.zoomChange.emit(next));
    };
    wrap.addEventListener("wheel", onWheel, { passive: false });
    this.wheelCleanup = () => wrap.removeEventListener("wheel", onWheel);
  }

  private applyZoomAnchor(nextZoom: number): void {
    const anchor = this.zoomAnchor;
    const wrap = this.wrapRef?.nativeElement;
    this.zoomAnchor = null;
    if (!anchor || !wrap || anchor.fromZoom <= 0) {
      return;
    }
    const ratio = nextZoom / anchor.fromZoom;
    requestAnimationFrame(() => {
      wrap.scrollLeft = Math.max(0, anchor.contentX * ratio - anchor.offsetX);
      wrap.scrollTop = Math.max(0, anchor.contentY * ratio - anchor.offsetY);
    });
  }

  private endPan(): void {
    this.panStart = null;
    if (this.isPanning) {
      this.isPanning = false;
      this.cdr.markForCheck();
    }
  }

  private clampZoom(zoom: number): number {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(zoom * 100) / 100));
  }

  private isEditableTarget(event: KeyboardEvent): boolean {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return false;
    }
    const tag = target.tagName;
    return (
      tag === "INPUT" ||
      tag === "TEXTAREA" ||
      tag === "SELECT" ||
      target.isContentEditable
    );
  }

  private scheduleUpdateFitScale(centerScroll = false): void {
    if (centerScroll) {
      this.centerScrollPending = true;
    }
    if (this.resizeFrameId) {
      return;
    }
    this.resizeFrameId = requestAnimationFrame(() => {
      this.resizeFrameId = 0;
      this.updateFitScale();
    });
  }

  private bindResizeObserver(): void {
    const wrap = this.wrapRef?.nativeElement;
    if (!wrap || typeof ResizeObserver === "undefined" || this.resizeObserver) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.runOutsideAngular(() => {
        this.scheduleUpdateFitScale();
      });
    });
    this.resizeObserver.observe(wrap);
  }

  private updateFitScale(): void {
    const wrap = this.wrapRef?.nativeElement;
    if (!wrap || !this.page) {
      return;
    }

    const stageWidth = wrap.offsetWidth;
    const stageHeight = wrap.offsetHeight;
    if (stageWidth < 1 || stageHeight < 1) {
      return;
    }

    const cardWidthPx = Math.max(1, this.page.widthCm * CM_TO_PX);
    const cardHeightPx = Math.max(1, this.page.heightCm * CM_TO_PX);
    const availW = Math.max(1, stageWidth - STAGE_PAD_X);
    const availH = Math.max(1, stageHeight - STAGE_PAD_Y);
    const nextFit = Math.max(
      MIN_FIT_SCALE,
      Math.round(Math.min(availW / cardWidthPx, availH / cardHeightPx) * 1000) /
        1000,
    );

    const fitChanged = Math.abs(nextFit - this.fitScale) >= 0.002;
    if (fitChanged) {
      this.fitScale = nextFit;
      this.ngZone.run(() => this.cdr.markForCheck());
    }

    if (this.centerScrollPending) {
      this.centerScrollPending = false;
      this.scheduleCenterScroll();
    }
  }

  private scheduleCenterScroll(): void {
    requestAnimationFrame(() => {
      const wrap = this.wrapRef?.nativeElement;
      if (!wrap) {
        return;
      }
      wrap.scrollLeft = Math.max(0, (wrap.scrollWidth - wrap.clientWidth) / 2);
      wrap.scrollTop = Math.max(0, (wrap.scrollHeight - wrap.clientHeight) / 2);
    });
  }

  private syncEditorFontFace(): void {
    const cssUrl = this.fontCssUrl?.trim() || "";
    const companyUrl = this.companyFontFileUrl?.trim() || "";
    const family = (this.fontFamily.split(",")[0] ?? "")
      .trim()
      .replace(/^["']+|["']+$/g, "");
    this.injectedFontNode?.remove();
    this.injectedFontNode = null;
    const token = ++this.fontReadyToken;
    if (cssUrl) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = cssUrl;
      link.setAttribute("data-print-card-editor-font", "1");
      link.onload = () => this.markEditorFontReady(token);
      link.onerror = () => this.markEditorFontReady(token);
      document.head.appendChild(link);
      this.injectedFontNode = link;
      return;
    }
    if (companyUrl && family) {
      const safeFamily = family.replace(/"/g, "");
      const style = document.createElement("style");
      style.setAttribute("data-print-card-editor-font", "1");
      style.textContent = `@font-face{font-family:"${safeFamily}";src:url("${companyUrl}");font-display:swap;}`;
      document.head.appendChild(style);
      this.injectedFontNode = style;
      this.markEditorFontReady(token);
      return;
    }
    this.markEditorFontReady(token);
  }

  private markEditorFontReady(token: number): void {
    void document.fonts.ready.then(() => {
      if (token !== this.fontReadyToken) {
        return;
      }
      this.ngZone.run(() => {
        this.fontEpoch += 1;
        this.cdr.markForCheck();
      });
    });
  }
}
